import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { fmtDateTime } from '../utils/date';
import type { Guard } from '../types';

interface Props {
  guards: Guard[];
  activeGuardIds?: Set<string>;
  onDeactivate?: (id: string) => void;
  onResetPin?: (id: string, newPin: string) => void;
  onDelete?: (id: string) => void;
  loading?: boolean;
  readOnly?: boolean;
}

export default function GuardTable({
  guards,
  activeGuardIds,
  onDeactivate,
  onResetPin,
  onDelete,
  loading,
  readOnly,
}: Props) {
  const role = useAuthStore((s) => s.role()) ?? 'admin';
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<string | null>(null);
  const [newPin, setNewPin] = useState('');

  function handleDelete(id: string) {
    if (confirmDelete === id) {
      onDelete?.(id);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(id);
    }
  }

  function handleResetPin(id: string) {
    if (!/^\d{4,6}$/.test(newPin)) return;
    onResetPin?.(id, newPin);
    setResetTarget(null);
    setNewPin('');
  }

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Loading patrollers…</div>;
  }

  if (guards.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        No patrollers found.{!readOnly && ' Add one to get started.'}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-gray-200">
            <th className="pb-2 pr-4 font-semibold text-gray-600">Name</th>
            <th className="pb-2 pr-4 font-semibold text-gray-600">Patrols</th>
            <th className="pb-2 pr-4 font-semibold text-gray-600">Events</th>
            <th className="pb-2 pr-4 font-semibold text-gray-600">Last patrol</th>
            <th className="pb-2 pr-4 font-semibold text-gray-600">Added</th>
            {!readOnly && <th className="pb-2 font-semibold text-gray-600">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {guards.map((g) => {
            const isPatrolling = activeGuardIds?.has(g.id) ?? false;
            const eventCount = (g.total_incidents ?? 0) + (g.total_alarms ?? 0);

            return (
              <tr
                key={g.id}
                className={`border-b border-gray-100 hover:bg-gray-50 ${!g.is_active ? 'opacity-60' : ''}`}
              >
                {/* Name — green dot if patrolling, strikethrough if inactive */}
                <td className="py-3 pr-4">
                  <span className="inline-flex items-center gap-2">
                    {isPatrolling && (
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-green-500"
                        title="On patrol"
                      />
                    )}
                    <span
                      className={`font-medium ${
                        g.is_active ? 'text-gray-900' : 'line-through text-gray-400'
                      }`}
                    >
                      {g.display_name}
                    </span>
                  </span>
                </td>

                {/* Patrol count — links to patrols filtered by this guard */}
                <td className="py-3 pr-4 tabular-nums">
                  {(g.total_patrols ?? 0) > 0 ? (
                    <Link
                      to={`/${role}/patrols?guard_id=${g.id}`}
                      className="text-brand-600 hover:underline font-medium"
                    >
                      {g.total_patrols}
                    </Link>
                  ) : (
                    <span className="text-gray-400">0</span>
                  )}
                </td>

                {/* Events count — links to events filtered by this guard */}
                <td className="py-3 pr-4 tabular-nums">
                  {eventCount > 0 ? (
                    <Link
                      to={`/${role}/events?guard_id=${g.id}`}
                      className="text-brand-600 hover:underline font-medium"
                    >
                      {eventCount}
                    </Link>
                  ) : (
                    <span className="text-gray-400">0</span>
                  )}
                </td>

                <td className="py-3 pr-4 text-gray-500">{fmtDateTime(g.last_patrol_at)}</td>
                <td className="py-3 pr-4 text-gray-500">
                  {fmtDateTime(g.created_at)}
                </td>

                {!readOnly && (
                  <td className="py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {g.is_active && (
                        <button
                          onClick={() => onDeactivate?.(g.id)}
                          className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800 hover:bg-yellow-200 transition-colors"
                        >
                          Deactivate
                        </button>
                      )}

                      {resetTarget === g.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={newPin}
                            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                            placeholder="4-6 digits"
                            className="w-24 border border-gray-300 rounded px-2 py-1 text-xs"
                            autoFocus
                          />
                          <button
                            onClick={() => handleResetPin(g.id)}
                            disabled={!/^\d{4,6}$/.test(newPin)}
                            className="px-2 py-1 text-xs rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setResetTarget(null);
                              setNewPin('');
                            }}
                            className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setResetTarget(g.id)}
                          className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                        >
                          Reset PIN
                        </button>
                      )}

                      <button
                        onClick={() => handleDelete(g.id)}
                        className={`px-2 py-1 text-xs rounded transition-colors ${
                          confirmDelete === g.id
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                      >
                        {confirmDelete === g.id ? 'Confirm delete' : 'Delete'}
                      </button>
                      {confirmDelete === g.id && (
                        <button
                          onClick={() => setConfirmDelete(null)}
                          className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
